/**
 * Session state of the console: Firebase user → `GET /v1/me` → `users.role` (docs/03 §1: the
 * role is read from D1, never from the token). The role gate lives in `App.tsx`.
 */
import type { Role, UserDTO } from '@akpl/shared';
import { createContext, type ReactNode, use, useCallback, useEffect, useState } from 'react';

import { api, setTokenProvider } from '../api';
import type { CmsAuthClient, CmsAuthUser } from './client';

export type AuthStatus = 'loading' | 'signed-out' | 'signed-in' | 'error';

export interface AuthState {
  status: AuthStatus;
  /** The Firebase user, present as soon as sign-in succeeds. */
  account: CmsAuthUser | null;
  /** The `users` row behind that account; null until `/me` answers. */
  me: UserDTO | null;
  role: Role | null;
  /** Message of the last `/me` failure. */
  error: string | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  reload(): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const value = use(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

export function AuthProvider({ client, children }: { client: CmsAuthClient; children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [account, setAccount] = useState<CmsAuthUser | null>(null);
  const [me, setMe] = useState<UserDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setTokenProvider(() => client.getIdToken());
    return client.onAuthState((user) => {
      setAccount(user);
      if (!user) {
        setMe(null);
        setError(null);
        setStatus('signed-out');
      } else {
        setStatus('loading');
      }
    });
  }, [client]);

  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    api
      .me()
      .then((res) => {
        if (cancelled) return;
        setMe(res.user);
        setError(null);
        setStatus('signed-in');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMe(null);
        setError(err instanceof Error ? err.message : String(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [account, nonce]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await client.signIn(email, password);
    },
    [client],
  );

  const signOut = useCallback(async () => {
    await client.signOut();
    setMe(null);
    setAccount(null);
    setStatus('signed-out');
  }, [client]);

  const reload = useCallback(() => {
    setStatus('loading');
    setNonce((n) => n + 1);
  }, []);

  const value: AuthState = {
    status,
    account,
    me,
    role: me?.role ?? null,
    error,
    signIn,
    signOut,
    reload,
  };
  return <AuthContext value={value}>{children}</AuthContext>;
}

const RANK: Record<Role, number> = { member: 0, viewer: 1, editor: 2, admin: 3 };

/** `member` has no console access at all (docs/06 §1). */
export function hasCmsAccess(role: Role | null): boolean {
  return role !== null && RANK[role] >= RANK.viewer;
}

export function atLeast(role: Role | null, min: Role): boolean {
  return role !== null && RANK[role] >= RANK[min];
}
